import React from 'react';
import { Link } from 'react-router-dom';
import { Shield, ArrowLeft } from 'lucide-react';

export const PrivacyPolicyPage = () => {
  return (
    <div className="bg-[var(--bg-primary)] min-h-screen pt-24 md:pt-32 pb-16 md:pb-24 px-4 md:px-8 lg:px-12">
      <div className="max-w-4xl mx-auto">
        {/* Back Link */}
        <Link 
          to="/" 
          className="inline-flex items-center gap-2 text-white/60 hover:text-[#2a5ff6] transition-colors mb-8"
        >
          <ArrowLeft size={18} />
          Back to Home
        </Link>

        {/* Header */}
        <div className="mb-12">
          <div className="inline-flex items-center gap-2 bg-[#2a5ff6]/10 border border-[#2a5ff6]/30 px-4 py-2 mb-6 text-sm text-[#2a5ff6] rounded-full">
            <Shield size={16} />
            Legal
          </div>
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4">Privacy Policy</h1>
          <p className="text-white/60">Last updated: February 2, 2026</p>
        </div>

        {/* Content */}
        <div className="prose prose-invert prose-lg max-w-none">
          <div className="bg-[var(--bg-secondary)] border border-white/10 rounded-2xl p-6 md:p-8 space-y-8">
            
            <section>
              <p className="text-white/80 leading-relaxed">
                Parler Technologies, Inc. and its affiliates ("Parler," "we," "our," or "us") respect your privacy. This Privacy Policy describes how we collect, use, disclose, and safeguard information in connection with <strong className="text-white">Parler Cloud</strong>, including our websites, cloud infrastructure services, content delivery services, security services, and related offerings (collectively, the "Services").
              </p>
              <p className="text-white/80 leading-relaxed mt-4">
                This Privacy Policy applies <strong className="text-white">only</strong> to Parler Cloud and does not apply to Parler's social media or consumer content platforms.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mb-4">1. Information We Collect</h2>
              
              <h3 className="text-xl font-semibold text-white mt-6 mb-3">A. Information You Provide</h3>
              <ul className="list-disc list-inside text-white/80 space-y-2">
                <li><strong className="text-white">Account Information:</strong> Name, business name, email address, phone number, billing address, and authentication credentials</li>
                <li><strong className="text-white">Billing Information:</strong> Payment method details, invoices, and transaction history (processed by third-party payment processors)</li>
                <li><strong className="text-white">Support Communications:</strong> Information you provide when contacting support, including tickets and correspondence</li>
              </ul>

              <h3 className="text-xl font-semibold text-white mt-6 mb-3">B. Information We Collect Automatically</h3>
              <ul className="list-disc list-inside text-white/80 space-y-2">
                <li><strong className="text-white">Service Usage Data:</strong> Resource utilization, performance metrics, configuration metadata, and operational telemetry</li>
                <li><strong className="text-white">Log Data:</strong> System logs, access logs, and error logs generated by the Services</li>
                <li><strong className="text-white">Device and Access Information:</strong> IP address, browser type, operating system, and timestamps</li>
              </ul>

              <div className="bg-[#2a5ff6]/10 border border-[#2a5ff6]/30 rounded-xl p-4 mt-6">
                <p className="text-white/80 text-sm leading-relaxed">
                  <strong className="text-white">Note:</strong> Parler Cloud does <strong className="text-white">not</strong> monitor, access, or control customer content deployed on the Services except as necessary to operate, secure, or support the Services or as required by law.
                </p>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mb-4">2. How We Use Information</h2>
              <p className="text-white/80 leading-relaxed mb-4">We use information to:</p>
              <ul className="list-disc list-inside text-white/80 space-y-2">
                <li>Provide, operate, and maintain the Services</li>
                <li>Authenticate users and secure accounts</li>
                <li>Process billing and payments</li>
                <li>Monitor performance, availability, and capacity</li>
                <li>Detect, prevent, and respond to security incidents, abuse, or fraud</li>
                <li>Provide customer support and respond to inquiries</li>
                <li>Comply with legal obligations and enforce our agreements</li>
              </ul>
              <p className="text-white/80 leading-relaxed mt-4">
                We do <strong className="text-white">not</strong> use Parler Cloud data for advertising, behavioral profiling, or social content recommendations.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mb-4">3. How We Share Information</h2>
              <p className="text-white/80 leading-relaxed mb-4">We may share information with:</p>
              <ul className="list-disc list-inside text-white/80 space-y-2">
                <li><strong className="text-white">Service Providers:</strong> Vendors supporting billing, infrastructure, monitoring, analytics, or customer support</li>
                <li><strong className="text-white">Legal and Regulatory Authorities:</strong> When required by law or legal process</li>
                <li><strong className="text-white">Business Transfers:</strong> In connection with mergers, acquisitions, or asset sales</li>
              </ul>
              <p className="text-white/80 leading-relaxed mt-4">
                We do <strong className="text-white">not</strong> sell personal information or share it for third-party marketing purposes.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mb-4">4. Data Security</h2>
              <p className="text-white/80 leading-relaxed">
                We implement reasonable administrative, technical, and physical safeguards designed to protect information processed through the Services. However, no system is completely secure, and we cannot guarantee absolute security.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mb-4">5. Data Retention</h2>
              <p className="text-white/80 leading-relaxed mb-4">We retain information only as long as necessary to:</p>
              <ul className="list-disc list-inside text-white/80 space-y-2">
                <li>Provide the Services</li>
                <li>Meet legal and regulatory requirements</li>
                <li>Resolve disputes</li>
                <li>Enforce our agreements</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mb-4">6. Your Rights</h2>
              <p className="text-white/80 leading-relaxed mb-4">Depending on your jurisdiction, you may have rights to:</p>
              <ul className="list-disc list-inside text-white/80 space-y-2">
                <li>Access your personal information</li>
                <li>Correct inaccuracies</li>
                <li>Request deletion of personal information</li>
                <li>Restrict or object to certain processing</li>
              </ul>
              <p className="text-white/80 leading-relaxed mt-4">
                Requests may be submitted using the contact information below.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mb-4">7. Children's Data</h2>
              <p className="text-white/80 leading-relaxed">
                Parler Cloud is not intended for use by individuals under 18 years of age, and we do not knowingly collect personal information from children.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mb-4">8. Changes to This Policy</h2>
              <p className="text-white/80 leading-relaxed">
                We may update this Privacy Policy from time to time. Material changes will be posted on this page, and continued use of the Services constitutes acceptance of the updated policy.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mb-4">9. Contact Information</h2>
              <p className="text-white/80 leading-relaxed">
                For privacy-related inquiries:
              </p>
              <div className="mt-4 p-4 bg-white/5 rounded-xl">
                <p className="text-white font-semibold mb-2">Parler Cloud Privacy</p>
                <p className="text-white/80">
                  <strong className="text-white">Email:</strong>{' '}
                  <a href="mailto:privacy@parler.com" className="text-[#2a5ff6] hover:underline">privacy@parler.com</a>
                </p>
                <p className="text-white/80 mt-2">
                  <strong className="text-white">Address:</strong><br />
                  2801 Network Boulevard, Suite 300<br />
                  Frisco, TX 75034
                </p>
              </div>
            </section>

          </div>
        </div>

        {/* Related Links */}
        <div className="mt-12 pt-8 border-t border-white/10">
          <h3 className="text-lg font-semibold text-white mb-4">Related Documents</h3>
          <div className="flex flex-wrap gap-4">
            <Link to="/terms" className="text-[#2a5ff6] hover:underline">Terms of Service</Link>
            <Link to="/do-not-sell" className="text-[#2a5ff6] hover:underline">Do Not Sell My Information</Link>
            <Link to="/contact" className="text-[#2a5ff6] hover:underline">Contact Us</Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicyPage;
